import {
  Component,
  EventEmitter,
  OnInit,
  viewChild,
  inject,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { take } from 'rxjs/operators';
import { EXTERNAL_LIST_DETAIL_CONTAINER } from 'src/app/constants/common.constants';
import {
  LazyLoadFilters,
  PageRequest,
  SearchContainerComponent,
} from 'src/app/core/containers/search-container/search-container.component';
import { TabService } from 'src/app/core/services/tab.service';
import {
  ExternalContactList,
  ExternalContactListSortOrQuery,
} from 'src/app/model/contacts/external-contact-list';
import { ContactListApiService } from 'src/app/services/api/contact-list-api.service';
import { AuthApiService } from 'src/app/services/api/auth-api.service';
import { ContactListBuilderService } from 'src/app/services/builder/contact-list-builder.service';
import { ExternalContactListTableService } from 'src/app/services/table/external-contact-list-table.service';
import { getExternalContactListColumns } from '../../definitions/external-contact-list-table-definition';
import { BreadCrumbBuilderService } from 'src/app/services/builder/breadcrumb-builder.service';
import { BreadCrumb } from 'src/app/model/common/bread-crumb';
import { BusinessUnitFilterComponent } from 'src/app/core/components/business-unit-filter/business-unit-filter.component';
import { CLAIMS } from 'src/app/constants/auth-constants';
import { Subscription } from 'rxjs';
import { isNullOrUndefined } from 'src/app/utils/utils';
import { GridLazyLoadEvent } from 'src/app/core/components/base-grid.component';
import { BlockUI } from '@openng/optimus-ui/blockui';
import { BusinessUnitFilterComponent as BusinessUnitFilterComponent_1 } from '../../../core/components/business-unit-filter/business-unit-filter.component';
import { ButtonDirective } from '@openng/optimus-ui/button';
import { Panel } from '@openng/optimus-ui/panel';
import { ExternalContactListMobileFilterComponent } from '../../components/external-contact-list-mobile-filter/external-contact-list-mobile-filter.component';
import { ProgressSpinnerComponent } from '../../../core/components/progress-spinner.component';
import { GeneralGridComponent } from '../../../core/components/general-grid.component';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';

@Component({
  selector: 'app-external-contact-list-search-container',
  templateUrl: './external-contact-list-search-container.component.html',
  styleUrls: ['./external-contact-list-search-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    BlockUI,
    BusinessUnitFilterComponent_1,
    ButtonDirective,
    Panel,
    ExternalContactListMobileFilterComponent,
    ProgressSpinnerComponent,
    GeneralGridComponent,
    TranslatePipe,
  ],
})
export class ExternalContactListSearchContainerComponent
  extends SearchContainerComponent<ExternalContactList>
  implements OnInit, OnDestroy
{
  protected _translateService = inject(TranslateService);
  protected _breadCrumbBuilderService = inject(BreadCrumbBuilderService);
  protected _contactListApi = inject(ContactListApiService);
  protected _contactListBuilder = inject(ContactListBuilderService);
  protected _contactListTableService = inject(ExternalContactListTableService);
  protected _authApi = inject(AuthApiService);
  protected _tabService = inject(TabService);
  protected _logAndMessage = inject(LogAndMessageService);

  readonly businessUnitFilter =
    viewChild<BusinessUnitFilterComponent>('buFilter');

  allContactLists: any[] = [];
  contactsLoading: boolean = true;
  availableCompanies: any[] = [];
  contactsLoadingEvent = new EventEmitter<any>();
  canEdit = false;
  scrollHeight = 'calc(100vh - 256px)';
  mobileFilterCollapsed = true;
  activeTabChangedSub!: Subscription;

  constructor() {
    super();
  }

  ngOnInit() {
    this.setupActiveTabChangedSubscription();
    this.setTableService(this._contactListTableService);
    this.initialize();
  }

  ngOnDestroy(): void {
    if (this.activeTabChangedSub) {
      this.activeTabChangedSub.unsubscribe();
    }
  }

  initialize() {
    this.canEdit = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.CONTACTS.EXTERNAL_LISTS.EDIT_EXTERNAL_LISTS,
    ]);
    this._translateService
      .get('CONTACT.SCREEN.EXTERNAL_CONTACT_LIST')
      .subscribe((label) => {
        if (this._deviceService.isMobile()) {
          this._breadCrumbBuilderService.resetAndAddBreadCrumb(
            new BreadCrumb(label, null, false)
          );
        } else {
          this._tabService.updateActiveTabLabel(label);
        }
        this.screenName = label;
      });

    if (this._deviceService.isMobile()) {
      this.scrollHeight = 'calc(100vh - 180px)';
    }

    this.columns = getExternalContactListColumns();
    this.applyPreviousData();
    this.applyPreviousFilters();
  }

  /**
   * Method called when a row double click event is fired.  This navigates to the details page.
   * @param $event - Event from the double click event.
   */
  changeViewEvent($event: { data: ExternalContactList }) {
    this._contactListTableService.setSelected(null); // make sure we grab by id after it opens
    const tab = this._tabService.buildNewTab(
      EXTERNAL_LIST_DETAIL_CONTAINER,
      true,
      undefined,
      $event.data.id
    );
    if (this._deviceService.isMobile()) {
      this._tabService.setMobileTab(tab);
    } else {
      this._tabService.openTab(tab);
    }
  }

  newContactList() {
    const ext = this._contactListBuilder.buildNullExternalContactList();
    this._contactListTableService.setSelected(ext);
    const tab = this._tabService.buildNewTab(
      EXTERNAL_LIST_DETAIL_CONTAINER,
      true
    );
    this._tabService.openTab(tab);
  }

  queryNetwork(
    req: PageRequest,
    $event: GridLazyLoadEvent,
    query: { [key: string]: any }
  ) {
    req.page += 1;
    const sort = this.buildNetworkSortQuery($event, 'name');

    this._contactListApi
      .queryForExternalContactLists(
        req.pageSize,
        req.page,
        query as ExternalContactListSortOrQuery,
        sort
      )
      .subscribe({
        next: (connection) => {
          this.allContactLists = connection.items;
          this.elements = [...this.allContactLists];
          this.finalizeQuery(connection.totalRecords);
        },
        error: (error) => {
          console.error(error);
          this._logAndMessage.translateToErrorAlert({
            headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
            bodyKey: 'CONTACT.MESSAGES.ERROR.FAILED_QUERY_EXTERNAL_LISTS',
          });
        },
      });
  }

  override handleLazyLoad(
    req: PageRequest,
    $event: GridLazyLoadEvent,
    filters: LazyLoadFilters
  ) {
    let selectedBusinessUnits = [];
    const businessUnitFilter = this.businessUnitFilter();
    if (businessUnitFilter?.selectedBusinessUnits) {
      // Handle further refreshes & changes to business unit filter component
      selectedBusinessUnits = businessUnitFilter.selectedBusinessUnits;
    } else {
      // Handle initial page load (before the business unit filter subcomponent exists)
      selectedBusinessUnits = this._authApi
        .getUserBusinessUnits()
        .map((bu) => bu.id);
    }
    filters['businessUnits'] = selectedBusinessUnits as unknown as any;

    const query =
      this._contactListBuilder.buildExternalContactListQuery(filters);
    this._contactListTableService.setLastQuery(query);
    this.queryNetwork(req, $event, query);
  }

  mobileFilter($event: any) {
    const newLazy: any = {
      filters: {
        ...this.grid()?.table()?.filters,
        name: $event.name
          ? {
              value: $event.name,
              matchMode: 'contains',
            }
          : null,
      },
    };
    this.mobileFilterCollapsed = true;
    this._contactListTableService.setLastLazyLoad(newLazy);
    this.lazyLoad({ lazy: this._contactListTableService.getLastLazyLoad()! });
  }

  refresh(): void {
    this.lazyLoad({ lazy: this._contactListTableService.getLastLazyLoad()! });
  }

  handleBusinessUnitChange() {
    this.lazyLoad({ lazy: this._contactListTableService.getLastLazyLoad()! });
  }

  setupActiveTabChangedSubscription(): void {
    this.activeTabChangedSub = this._tabService.activeTabChanged.subscribe(
      (tab) => {
        if (!isNullOrUndefined(tab) && tab.header === this.screenName) {
          this.refresh();
        }
      }
    );
  }
}
